import { useRef, useState } from 'react';
import { CheckOutlined, EditOutlined, HistoryOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, App, Button, DatePicker, Descriptions, Drawer, Form, Input, Modal, Select, Space, Tabs, Tag, Timeline } from 'antd';
import dayjs from 'dayjs';
import { api } from '../api';
import { DataTable, ErrorNotice, PageHeader, Panel, RowActions } from '../components';
import { formatDate, formatDateTime, useLoad } from '../lib';
import { HsnTab } from './GstHsnTab';
/** Kivonex Admin › GST: the taxpayer register the app checks GSTINs against, imports and the GST audit. */
const STATUSES = ['Active', 'Cancelled', 'Suspended', 'Inactive', 'Unknown'];
const STALE_DAYS = 90;
const SOURCE_LABEL = {
    LOCAL_VALIDATION: { text: 'Format only', color: 'default' },
    USER_VERIFIED: { text: 'Verified by a person', color: 'blue' },
    IMPORTED_DOCUMENT: { text: 'Imported', color: 'gold' },
    AUTHORIZED_PROVIDER: { text: 'From GSTN (provider)', color: 'green' }
};
const isStale = (row) => dayjs().diff(dayjs(row.refreshedAt), 'day') > STALE_DAYS;
function SourceTag({ source }) {
    const label = SOURCE_LABEL[source];
    return <Tag color={label.color}>{label.text}</Tag>;
}
function errorText(err, fallback) {
    return err instanceof Error ? err.message : fallback;
}
function TaxpayersTab() {
    const { message } = App.useApp();
    const [query, setQuery] = useState('');
    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const { data, error, loading, reload } = useLoad(() => api(`/v1/admin/gst/taxpayers?q=${encodeURIComponent(query)}&limit=300`).then((body) => body.taxpayers), [query]);
    const detail = useLoad(() => (viewing ? api(`/v1/admin/gst/taxpayers/${viewing}`) : Promise.resolve(null)), [viewing]);
    async function save(values) {
        setSaving(true);
        try {
            await api('/v1/admin/gst/taxpayers', {
                method: 'POST',
                body: JSON.stringify({
                    ...values,
                    registrationDate: values.registrationDate ? values.registrationDate.format('YYYY-MM-DD') : null,
                    cancellationDate: values.cancellationDate ? values.cancellationDate.format('YYYY-MM-DD') : null
                })
            });
            message.success(`${values.legalName} saved as verified by you`);
            setEditing(null);
            await reload();
            if (viewing)
                await detail.reload();
        }
        catch (err) {
            message.error(errorText(err, 'Could not save the taxpayer'));
        }
        finally {
            setSaving(false);
        }
    }
    async function checked(row) {
        try {
            await api(`/v1/admin/gst/taxpayers/${row.gstin}/refresh`, { method: 'POST' });
            message.success(`${row.legalName} marked as checked today`);
            await reload();
        }
        catch (err) {
            message.error(errorText(err, 'Could not update the taxpayer'));
        }
    }
    async function removeOverride() {
        try {
            await api(`/v1/admin/gst/taxpayers/${viewing}/manual-override`, { method: 'DELETE' });
            message.success('Manual override removed; WhiteBooks data is active again');
            await Promise.all([reload(), detail.reload()]);
        }
        catch (err) {
            message.error(errorText(err, 'Could not remove the manual override'));
        }
    }
    const initial = (row) => row && row !== 'new'
        ? {
            ...row,
            registrationDate: row.registrationDate ? dayjs(row.registrationDate) : null,
            cancellationDate: row.cancellationDate ? dayjs(row.cancellationDate) : null,
            note: ''
        }
        : { status: 'Active', natureOfBusiness: [], tradeName: '', constitution: '', taxpayerType: '', address: '', jurisdiction: '', note: '' };
    const detailRecord = detail.data?.manualOverride ?? detail.data?.record;
    return (<>
      <div className="toolbar" style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Input.Search allowClear placeholder="GSTIN or name" onSearch={(value) => setQuery(value.trim())} style={{ maxWidth: 360 }}/>
        <span style={{ flex: 1 }}/>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')}>
          Add taxpayer
        </Button>
      </div>
      <ErrorNotice error={error}/>
      <Panel flush>
        <DataTable rowKey="gstin" loading={loading} dataSource={data ?? []} pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1000 }} onRow={(row) => ({ onClick: () => setViewing(row.gstin), style: { cursor: 'pointer' } })} locale={{ emptyText: query ? 'No taxpayer matches.' : 'The register is empty. Add a taxpayer you checked, or import a file.' }} columns={[
            {
                title: 'Taxpayer',
                skeleton: 'lines',
                render: (_value, row) => (<span>
                  <strong style={{ fontWeight: 550 }}>{row.legalName}</strong>
                  <span className="cell-sub mono">{row.gstin}</span>
                </span>)
            },
            { title: 'Trade name', dataIndex: 'tradeName', width: 180 },
            { title: 'Status', dataIndex: 'status', width: 110, skeleton: 'tag', render: (value) => <Tag color={value === 'Active' ? 'green' : value === 'Unknown' ? 'default' : 'red'}>{value}</Tag> },
            { title: 'Source', dataIndex: 'source', width: 190, skeleton: 'tag', render: (value) => <SourceTag source={value}/> },
            {
                title: 'Last checked',
                dataIndex: 'refreshedAt',
                width: 150,
                render: (value, row) => (<span>
                  {formatDate(value)}
                  {isStale(row) && <span className="cell-sub" style={{ color: '#b45309' }}>Over {STALE_DAYS} days: check again</span>}
                </span>)
            },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for ${row.legalName}`} actions={[
                        { key: 'view', icon: <HistoryOutlined />, label: 'Details and history', onClick: () => setViewing(row.gstin) },
                        { key: 'edit', icon: <EditOutlined />, label: row.source === 'AUTHORIZED_PROVIDER' ? 'Create manual override' : 'Correct details', hint: row.source === 'AUTHORIZED_PROVIDER' ? 'Keeps the WhiteBooks snapshot and history unchanged' : 'Saved as verified by you', onClick: () => setEditing(row) },
                        row.source === 'USER_VERIFIED' && {
                            key: 'checked',
                            icon: <CheckOutlined />,
                            label: 'Checked again today',
                            hint: 'Details unchanged on the certificate or portal search',
                            onClick: () => checked(row)
                        }
                    ]}/>)
            }
        ]}/>
      </Panel>

      <Drawer title={detail.data?.record?.legalName ?? viewing ?? ''} open={viewing !== null} onClose={() => setViewing(null)} size={560} destroyOnHidden>
        <ErrorNotice error={detail.error}/>
        {detail.data && (<>
            <Alert type={detail.data.status === 'FOUND' && !detail.data.stale ? 'info' : 'warning'} showIcon title={detail.data.status === 'FOUND' ? (detail.data.stale ? 'On record, but stale' : 'On record') : 'Verification required'} description={detail.data.basis} style={{ marginBottom: 16 }}/>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="GSTIN">
                <span className="mono">{detail.data.validation.gstin}</span>
              </Descriptions.Item>
              <Descriptions.Item label="State">{detail.data.validation.stateName}</Descriptions.Item>
              <Descriptions.Item label="PAN">
                {detail.data.validation.pan} {detail.data.validation.panHolder && <span className="cell-sub">{detail.data.validation.panHolder}</span>}
              </Descriptions.Item>
                  {detailRecord && (<>
                  <Descriptions.Item label="Trade name">{detailRecord.tradeName || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Constitution">{detailRecord.constitution || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Taxpayer type">{detailRecord.taxpayerType || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Status">{detailRecord.status}</Descriptions.Item>
                  <Descriptions.Item label="Registered">{detailRecord.registrationDate ? formatDate(detailRecord.registrationDate) : '—'}</Descriptions.Item>
                  {detailRecord.cancellationDate && <Descriptions.Item label="Cancelled">{formatDate(detailRecord.cancellationDate)}</Descriptions.Item>}
                  <Descriptions.Item label="Address">{detailRecord.address || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Business">{detailRecord.natureOfBusiness.join(', ') || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Jurisdiction">{detailRecord.jurisdiction || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Source">
                    <SourceTag source={detailRecord.source}/>
                  </Descriptions.Item>
                </>)}
            </Descriptions>
            {detail.data.manualOverride && (
              <Alert type="warning" showIcon title="Manual override is active" description={<span>WhiteBooks snapshots remain in history and can still be refreshed. <Button type="link" danger onClick={removeOverride}>Remove override</Button></span>} style={{ marginTop: 12 }}/>
            )}
            {detail.data.validation.warnings.map((warning) => (<Alert key={warning} type="warning" title={warning} style={{ marginTop: 12 }}/>))}
            <h4 style={{ margin: '20px 0 12px' }}>History</h4>
            <Timeline items={detail.data.history.map((row) => ({
                content: (<span>
                    <SourceTag source={row.source}/> {row.note}
                    <span className="cell-sub">
                      {row.by ?? 'system'} · {formatDateTime(row.at)}
                    </span>
                  </span>)
            }))}/>
          </>)}
      </Drawer>

      <Modal title={editing === 'new' ? 'Add taxpayer' : `Correct ${editing ? editing.legalName : ''}`} open={editing !== null} onCancel={() => setEditing(null)} onOk={() => form.submit()} okText="Save as verified" confirmLoading={saving} width={640} destroyOnHidden>
        <Alert type="info" showIcon title="Enter only what you read on the registration certificate or the GST portal's public search. Leave a field blank rather than guess." style={{ marginBottom: 16 }}/>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={save} initialValues={initial(editing)}>
          <div className="form-row">
            <Form.Item label="GSTIN" name="gstin" normalize={(value) => value.toUpperCase().replace(/\s+/g, '')} rules={[{ required: true, len: 15, message: 'A GSTIN has 15 characters.' }]}>
              <Input placeholder="19ABCDE1234F1Z5" className="mono" maxLength={15} disabled={editing !== 'new'} autoFocus={editing === 'new'} autoComplete="off"/>
            </Form.Item>
            <Form.Item label="Status" name="status">
              <Select placeholder="Choose status" options={STATUSES.map((value) => ({ value, label: value }))}/>
            </Form.Item>
          </div>
          <Form.Item label="Legal name" name="legalName" rules={[{ required: true, whitespace: true, max: 200, message: 'Enter the legal name.' }]}>
            <Input placeholder="As on the GST certificate" autoFocus={editing !== 'new'}/>
          </Form.Item>
          <div className="form-row">
            <Form.Item label="Trade name" name="tradeName" rules={[{ max: 200 }]}>
              <Input placeholder="Name the business trades under" />
            </Form.Item>
            <Form.Item label="Constitution" name="constitution" rules={[{ max: 80 }]}>
              <Input placeholder="Proprietorship, Partnership, Private Limited Company…"/>
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item label="Taxpayer type" name="taxpayerType" rules={[{ max: 60 }]}>
              <Input placeholder="Regular, Composition…"/>
            </Form.Item>
            <Form.Item label="Jurisdiction" name="jurisdiction" rules={[{ max: 200 }]}>
              <Input placeholder="State or Centre jurisdiction" />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item label="Date of registration" name="registrationDate">
              <DatePicker placeholder="DD-MM-YYYY" format="DD-MM-YYYY" style={{ width: '100%' }}/>
            </Form.Item>
            <Form.Item label="Date of cancellation" name="cancellationDate">
              <DatePicker placeholder="DD-MM-YYYY" format="DD-MM-YYYY" style={{ width: '100%' }}/>
            </Form.Item>
          </div>
          <Form.Item label="Principal place of business" name="address" rules={[{ max: 500 }]}>
            <Input.TextArea placeholder="Building, street, city, PIN code" rows={2}/>
          </Form.Item>
          <Form.Item label="Nature of business" name="natureOfBusiness">
            <Select mode="tags" tokenSeparators={[',', ';']} placeholder="Type and press Enter"/>
          </Form.Item>
          <Form.Item label="Checked against" name="note" rules={[{ required: true, whitespace: true, max: 500, message: 'Say where you read these details.' }]}>
            <Input placeholder="Registration certificate (REG-06) from the party, 24-10-2026"/>
          </Form.Item>
        </Form>
      </Modal>
    </>);
}
// ------------------------------------------------------------------ import
function ImportTab() {
    const { message } = App.useApp();
    const fileInput = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const imports = useLoad(() => api('/v1/admin/gst/imports').then((body) => body.imports));
    async function pick(chosen) {
        if (!chosen)
            return;
        if (chosen.size > 4 * 1024 * 1024)
            return message.error('The file is over 4 MB. Split it into smaller files.');
        const csv = await chosen.text();
        setFile({ name: chosen.name, csv });
        setBusy(true);
        try {
            setPreview(await api('/v1/admin/gst/taxpayers/import', { method: 'POST', body: JSON.stringify({ fileName: chosen.name, csv, preview: true }) }));
        }
        catch (err) {
            setPreview(null);
            message.error(errorText(err, 'Could not read the file'));
        }
        finally {
            setBusy(false);
        }
    }
    async function run() {
        if (!file)
            return;
        setBusy(true);
        try {
            const body = await api('/v1/admin/gst/taxpayers/import', { method: 'POST', body: JSON.stringify({ fileName: file.name, csv: file.csv, preview: false }) });
            message.success(`${body.batch.accepted} taxpayers imported; ${body.batch.rejected} rows left out`);
            setFile(null);
            setPreview(null);
            await imports.reload();
        }
        catch (err) {
            message.error(errorText(err, 'Could not import the file'));
        }
        finally {
            setBusy(false);
        }
    }
    return (<>
      <Panel title="Import a file">
        <p style={{ marginTop: 0 }}>
          A CSV with a header row: <span className="mono">GSTIN, Legal Name, Trade Name, Constitution, Taxpayer Type, Date of Registration, Date of Cancellation, Status, Address, Nature of Business, Jurisdiction</span>.
          Only GSTIN and Legal Name are required; separate several natures of business with a semicolon. Imported rows are marked
          &ldquo;Imported&rdquo;, never as verified with GSTN, and taxpayers a person already verified are left as they are.
        </p>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(event) => void pick(event.target.files?.[0]).finally(() => (event.target.value = ''))}/>
        <Button icon={<UploadOutlined />} onClick={() => fileInput.current?.click()} loading={busy && !preview}>
          Choose CSV file
        </Button>
        {preview && (<div style={{ marginTop: 16 }}>
            <Alert type={preview.rejected ? 'warning' : 'success'} showIcon title={`${preview.fileName}: ${preview.rows} rows, ${preview.accepted} ready to import, ${preview.rejected} left out`} action={<Button type="primary" onClick={run} loading={busy} disabled={!preview.accepted}>
                  Import {preview.accepted}
                </Button>}/>
            {preview.errors.length > 0 && (<DataTable style={{ marginTop: 12 }} rowKey={(row) => `${row.row}`} loading={false} size="small" dataSource={preview.errors} pagination={{ pageSize: 20, hideOnSinglePage: true }} columns={[
                    { title: 'Row', dataIndex: 'row', width: 70 },
                    { title: 'GSTIN', dataIndex: 'gstin', width: 180, render: (value) => <span className="mono">{value}</span> },
                    { title: 'Why it was left out', dataIndex: 'message' }
                ]}/>)}
          </div>)}
      </Panel>
      <Panel title="Earlier imports" flush>
        <ErrorNotice error={imports.error}/>
        <DataTable rowKey="id" loading={imports.loading} skeletonRows={3} dataSource={imports.data ?? []} pagination={false} locale={{ emptyText: 'No imports yet.' }} columns={[
            { title: 'File', dataIndex: 'fileName' },
            { title: 'Rows', dataIndex: 'rows', width: 90, align: 'right' },
            { title: 'Imported', dataIndex: 'accepted', width: 100, align: 'right' },
            { title: 'Left out', dataIndex: 'rejected', width: 100, align: 'right' },
            { title: 'By', dataIndex: 'by', width: 160 },
            { title: 'When', dataIndex: 'at', width: 170, render: (value) => formatDateTime(value) }
        ]}/>
      </Panel>
    </>);
}
// ------------------------------------------------------------------ audit
function AuditTab() {
    const { data, error, loading } = useLoad(() => api('/v1/admin/gst/audit?limit=500').then((body) => body.audit));
    const calls = useLoad(() => api('/v1/admin/gst/api-calls?limit=500').then((body) => body.calls));
    const [query, setQuery] = useState('');
    const [outcome, setOutcome] = useState('all');
    const filteredCalls = (calls.data ?? []).filter((row) => {
        const needle = query.trim().toLowerCase();
        return (outcome === 'all' || row.status === outcome)
            && (!needle || `${row.operation} ${row.accountId} ${row.actorId} ${row.providerRequestId} ${row.errorCode} ${row.errorMessage}`.toLowerCase().includes(needle));
    });
    return (<>
      <ErrorNotice error={error}/>
      <Panel flush>
        <DataTable rowKey="id" loading={loading} dataSource={data ?? []} pagination={{ pageSize: 50, hideOnSinglePage: true }} locale={{ emptyText: 'No GST calls yet.' }} columns={[
            { title: 'When', dataIndex: 'at', width: 170, render: (value) => formatDateTime(value) },
            { title: 'Who', dataIndex: 'actorId', render: (value, row) => <span>
                  {value}
                  <span className="cell-sub">{row.actorType === 'admin' ? 'Admin' : 'App'}</span>
                </span> },
            { title: 'Action', dataIndex: 'action', width: 170 },
            { title: 'GSTIN', dataIndex: 'gstin', width: 170, render: (value) => <span className="mono">{value || '—'}</span> },
            { title: 'Result', dataIndex: 'result', width: 200 }
        ]}/>
      </Panel>
      <Panel title="WhiteBooks calls" description="Operational metadata only; credentials, tokens and document payloads are never retained." flush>
        <ErrorNotice error={calls.error}/>
        <Space wrap style={{ padding: 12 }}>
          <Input.Search allowClear placeholder="Operation, account, request ID or failure" onChange={(event) => setQuery(event.target.value)} style={{ width: 360 }}/>
          <Select value={outcome} onChange={setOutcome} options={[{ value: 'all', label: 'All outcomes' }, { value: 'success', label: 'Success' }, { value: 'failed', label: 'Failed' }, { value: 'fallback', label: 'Fallback' }, { value: 'not_found', label: 'Not found' }]} style={{ width: 150 }}/>
        </Space>
        <DataTable rowKey="id" loading={calls.loading} dataSource={filteredCalls} pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1050 }} locale={{ emptyText: 'No WhiteBooks calls match.' }} columns={[
            { title: 'When', dataIndex: 'at', width: 170, render: (value) => formatDateTime(value) },
            { title: 'Account / actor', render: (_value, row) => <span>{row.accountId || 'Admin'}<span className="cell-sub">{row.actorId}</span></span> },
            { title: 'Operation', dataIndex: 'operation', width: 180 },
            { title: 'GSTIN', dataIndex: 'gstin', width: 160, render: (value) => <span className="mono">{value || '—'}</span> },
            { title: 'Result', width: 170, render: (_value, row) => <span><Tag color={row.status === 'success' ? 'green' : row.status === 'fallback' ? 'gold' : 'red'}>{row.status}</Tag>{row.fallbackUsed && 'fallback'}</span> },
            { title: 'Time', dataIndex: 'durationMs', width: 90, align: 'right', render: (value) => `${value} ms` },
            { title: 'Request ID / error', render: (_value, row) => <span className="mono">{row.providerRequestId || row.errorCode || '—'}{row.errorMessage && <span className="cell-sub">{row.errorMessage}</span>}</span> }
        ]}/>
      </Panel>
    </>);
}
export function GstPage({ section, go }) {
    const tab = ['taxpayers', 'import', 'hsn', 'audit'].includes(section) ? section : 'taxpayers';
    return (<>
      <PageHeader title="GST" description="The taxpayer register and HSN/SAC rates the Kivonex app checks against. Every record says where it came from; nothing is shown as verified with GSTN unless it came from an authorised provider."/>
      <Tabs activeKey={tab} onChange={go} destroyOnHidden items={[
            { key: 'taxpayers', label: 'Taxpayers', children: <TaxpayersTab /> },
            { key: 'import', label: 'Import', children: <ImportTab /> },
            { key: 'hsn', label: 'HSN/SAC and rates', children: <HsnTab /> },
            { key: 'audit', label: 'Audit', children: <AuditTab /> }
        ]}/>
    </>);
}
